/* eslint-disable no-unused-vars */
import {ref} from 'vue'

export interface UseCcrsDropdownOptions {
  loadMoreProducts: () => Promise<void>
  loadMoreDesigns: () => Promise<void>
  itemNumbers: Ref<any[]>
  designs: Ref<any[]>
  productCount: Ref<number>
  designCount: Ref<number>
  isLoadingMore: Ref<boolean>
  isLoadingMoreDesigns: Ref<boolean>
}

const SCROLL_THRESHOLD = 50
const LOAD_COOLDOWN = 500

export const useCcrsDropdown = (options: UseCcrsDropdownOptions) => {
  const {
    loadMoreProducts,
    loadMoreDesigns,
    itemNumbers,
    designs,
    productCount,
    designCount,
    isLoadingMore,
    isLoadingMoreDesigns
  } = options

  // State
  const isOpen = ref(false)
  const dropdownRef = ref<HTMLElement | null>(null)
  const designsSectionRef = ref<HTMLElement | null>(null)

  // Timers
  let blurTimer: ReturnType<typeof setTimeout> | null = null
  let dropdownBlurTimer: ReturnType<typeof setTimeout> | null = null
  let scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null

  // Scroll tracking
  let lastScrollPosition = 0
  let lastProductLoadTime = 0
  let lastDesignLoadTime = 0

  // Touch interaction tracking
  let isTouchActive = false

  const clearBlurTimers = () => {
    if (blurTimer) {
      clearTimeout(blurTimer)
      blurTimer = null
    }
    if (dropdownBlurTimer) {
      clearTimeout(dropdownBlurTimer)
      dropdownBlurTimer = null
    }
  }

  const handleFocus = (searchQuery: string, hasResultsForCurrentTerm: () => boolean, triggerSearch: () => void) => {
    if (searchQuery.length > 0) {
      isOpen.value = true
      if (!hasResultsForCurrentTerm()) {
        triggerSearch()
      }
    }
  }

  const handleBlur = () => {
    if (blurTimer) {
      clearTimeout(blurTimer)
    }

    blurTimer = setTimeout(() => {
      // Don't close if touch interaction is active
      if (isTouchActive) {
        return
      }

      const activeElement = document.activeElement
      const dropdown = document.querySelector('.ccrs__dropdown')
      const input = document.getElementById('ccrs-search-input')

      if (activeElement === input || dropdown?.contains(activeElement)) {
        return
      }

      isOpen.value = false
    }, 200)
  }

  const handleDropdownFocusIn = () => {
    clearBlurTimers()
    isOpen.value = true
  }

  const handleDropdownFocusOut = () => {
    if (dropdownBlurTimer) {
      clearTimeout(dropdownBlurTimer)
    }

    dropdownBlurTimer = setTimeout(() => {
      // Don't close if touch interaction is active
      if (isTouchActive) {
        return
      }

      const activeElement = document.activeElement
      const dropdown = document.querySelector('.ccrs__dropdown')
      const input = document.getElementById('ccrs-search-input')

      if (activeElement !== input && !dropdown?.contains(activeElement)) {
        isOpen.value = false
      }
    }, 200)
  }

  const canLoadMoreProducts = (currentTime: number): boolean => {
    return !isLoadingMore.value &&
           itemNumbers.value.length < productCount.value &&
           currentTime - lastProductLoadTime >= LOAD_COOLDOWN
  }

  const canLoadMoreDesigns = (currentTime: number): boolean => {
    return !isLoadingMoreDesigns.value &&
           designs.value.length < designCount.value &&
           designs.value.length >= 5 &&
           currentTime - lastDesignLoadTime >= LOAD_COOLDOWN
  }

  const isDesignsSectionVisible = (dropdownTop: number, dropdownBottom: number): boolean => {
    if (!designsSectionRef.value) {return false}
    const designsRect = designsSectionRef.value.getBoundingClientRect()
    return designsRect.top <= dropdownBottom && designsRect.bottom >= dropdownTop
  }

  const tryLoadMoreProducts = (scrollBottom: number, dropdownTop: number, dropdownBottom: number): boolean => {
    if (isDesignsSectionVisible(dropdownTop, dropdownBottom)) {
      lastProductLoadTime = Date.now()
      loadMoreProducts()
      return true
    }

    if (designs.value.length === 0 && scrollBottom <= 200) {
      lastProductLoadTime = Date.now()
      loadMoreProducts()
      return true
    }

    return false
  }

  const handleDropdownScroll = () => {
    if (!dropdownRef.value) {return}

    if (scrollDebounceTimer) {
      clearTimeout(scrollDebounceTimer)
    }

    scrollDebounceTimer = setTimeout(() => {
      if (!dropdownRef.value) {return}

      const dropdown = dropdownRef.value
      const scrollTop = dropdown.scrollTop
      const scrollBottom = dropdown.scrollHeight - scrollTop - dropdown.clientHeight

      const scrollDelta = Math.abs(scrollTop - lastScrollPosition)
      if (scrollDelta < SCROLL_THRESHOLD) {return}

      lastScrollPosition = scrollTop

      const dropdownRect = dropdown.getBoundingClientRect()
      const currentTime = Date.now()

      // Check products section
      if (canLoadMoreProducts(currentTime)) {
        if (tryLoadMoreProducts(scrollBottom, dropdownRect.top, dropdownRect.bottom)) {
          return
        }
      }

      // Check designs section
      if (canLoadMoreDesigns(currentTime) && scrollBottom <= 150) {
        lastDesignLoadTime = Date.now()
        loadMoreDesigns()
      }
    }, 150)
  }

  const resetScrollTracking = () => {
    lastScrollPosition = 0
    lastProductLoadTime = 0
    lastDesignLoadTime = 0
  }

  const closeDropdown = () => {
    isOpen.value = false
  }

  const openDropdown = () => {
    isOpen.value = true
  }

  const handleTouchStart = () => {
    isTouchActive = true
  }

  const handleTouchEnd = () => {
    // Keep touch active state for a short period to prevent premature closing
    setTimeout(() => {
      isTouchActive = false
    }, 300)
  }

  const handleTouchMove = () => {
    isTouchActive = true
  }

  return {
    // State
    isOpen,
    dropdownRef,
    designsSectionRef,

    // Methods
    handleFocus,
    handleBlur,
    handleDropdownFocusIn,
    handleDropdownFocusOut,
    handleDropdownScroll,
    resetScrollTracking,
    closeDropdown,
    openDropdown,
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove
  }
}

export type UseCcrsDropdown = ReturnType<typeof useCcrsDropdown>
